import * as React from "react";
import styled from "styled-components";
import { processInlineFormatting } from "./RenderText";

/**
 * Zentrale wiederverwendbare Styled Components
 * Um Code-Duplikation zu vermeiden
 */

// Tab Navigation
export const TabContainer = styled.div`
  display: flex;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  gap: 4px;

  @media only screen and (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

export const TabButton = styled.button<{ isActive?: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: ${(props) => (props.isActive ? "default" : "pointer")};
  transition: all 0.3s ease;
  text-align: center;

  ${(props) =>
    props.isActive
      ? `
    background: var(--primaryColor);
    color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  `
      : `
    background: transparent;
    color: #666;
    
    &:hover {
      background: #e9ecef;
      color: #333;
    }
  `}

  @media only screen and (max-width: 768px) {
    font-size: 13px;
    padding: 10px 12px;
  }
`;

// Content Container
export const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

// Card Components
export const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  h2 {
    color: var(--primaryColor);
    margin-bottom: 16px;
  }

  h3 {
    color: #333;
    margin: 20px 0 12px 0;
  }

  h4 {
    color: var(--primaryColor);
    margin-bottom: 8px;
  }

  p {
    line-height: 1.6;
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      line-height: 1.5;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;

    th,
    td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e0e0e0;
    }

    th {
      background: #f5f5f5;
      font-weight: 600;
      color: #333;
    }

    tr:hover {
      background: #f9f9f9;
    }
  }
`;

// InfoBox
export const InfoBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8e9 100%);
  border-left: 4px solid var(--primaryColor);
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "INFO";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--primaryColor);
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: var(--primaryColor);
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }
`;

// Chart Section
export const ChartSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  text-align: center;
`;

// Welcome Card (für Übersichtsseiten)
export const WelcomeCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;

  h1 {
    color: var(--primaryColor);
    margin-bottom: 20px;
  }

  p {
    font-size: 16px;
    line-height: 1.6;
    color: #666;
    max-width: 800px;
    margin: 0 auto;
  }
`;

// Source Note
export const SourceNote = styled.div`
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-top: 15px;
  text-align: center;
`;

// Image Container
export const ImageContainer = styled.div`
  position: relative;
  text-align: center;
  margin: 30px 0;

  img {
    max-width: 75%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  @media only screen and (max-width: 768px) {
    img {
      max-width: 100%;
    }
  }
`;

// Einheitliche Abbildungsunterschrift
const StyledFigureCaption = styled.p`
  font-size: 13px;
  color: #666;
  font-style: italic;
  text-align: center;
  margin-top: 10px;
  margin-bottom: 0;
  line-height: 1.4;
`;

export const FigureCaption = React.memo(function FigureCaption({
  children,
}: {
  children: React.ReactNode;
}) {
  const processed = React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return processInlineFormatting(child);
    }
    return child;
  });
  return <StyledFigureCaption>{processed}</StyledFigureCaption>;
});

// Tip Card (für Spartipps)
export const TipCard = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 15px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "TIPP";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #2e7d32;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    margin-bottom: 8px;
    color: #333;
  }

  ul {
    padding-left: 20px;
    margin-top: 10px;

    li {
      margin-bottom: 6px;
      color: #555;
    }
  }
`;

// Price Box
export const PriceBox = styled.div`
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #ff9800;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "PREISINFO";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #e65100;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  h4 {
    color: #e65100;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
    color: #333;
  }

  .price-highlight {
    font-size: 24px;
    font-weight: bold;
    color: #e65100;
    margin: 10px 0;
  }
`;

// Section Block (mit Border)
export const SectionBlock = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  border-left: 6px solid var(--primaryColor);

  h2 {
    color: var(--primaryColor);
    margin-bottom: 20px;
    font-size: 24px;
  }
`;

// Illustration Section
export const IllustrationSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  text-align: center;
`;

// Sources Section (Literaturverzeichnis)
export const SourcesSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px 30px;
  margin-top: 20px;

  h3 {
    color: #666;
    font-size: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 8px;
  }

  ol {
    margin: 0;
    padding-left: 20px;

    li {
      font-size: 12px;
      color: #666;
      line-height: 1.6;
      margin-bottom: 8px;

      a {
        color: var(--primaryColor);
        word-break: break-all;
      }
    }
  }
`;
